import React, { useState } from 'react';
import { FieldProps, getIn } from 'formik'
import { InputAdornment, TextField, TextFieldProps, IconButton } from '@mui/material'
import { Visibility, VisibilityOff } from '@mui/icons-material';

// Match the field height and typography used by editable detail dialogs.
export const authFieldSx = { m: 0, fontSize: '16px' };
export const authInputSx = { fontSize: '16px', '& input': { padding: 1, paddingX: 2 } };
export const authLabelProps = { shrink: true as const, style: { fontSize: '16px' } };

export function handlePasswordVisibilityShortcut(event: React.KeyboardEvent, toggle: () => void) {
  if (event.altKey && event.shiftKey && event.code === 'KeyV') {
    event.preventDefault();
    toggle();
  }
}

/**
 * Material TextField Component with Formik Support including Errors.
 * Intended to be specified via the `component` prop in a Formik <Field data-testid="Field-2lrx"> or <FastField> component.
 * Material-UI specific props are passed through.
 */
export const FormEmailTextField: React.FC<FieldProps & TextFieldProps> = props => {
  const isTouched = getIn(props.form.touched, props.field.name)
  const errorMessage = getIn(props.form.errors, props.field.name)

  const { error, helperText, field, form, ...rest } = props

  return (
    <TextField data-testid="TextField-twge"
      //   variant="outlined"
      error={error ?? Boolean(isTouched && errorMessage)}
      helperText={helperText ?? ((isTouched && errorMessage) ? errorMessage : undefined)}
      {...rest}
      {...field}
      fullWidth
      variant="outlined"
      sx={authFieldSx}
      InputProps={{ sx: authInputSx }}
      InputLabelProps={authLabelProps}
    />
  )
}

export const FormPasswordTextField: React.FC<FieldProps & TextFieldProps> = props => {
  const [showPassword, setShowPassword] = useState(false);

  const isTouched = getIn(props.form.touched, props.field.name);
  const errorMessage = getIn(props.form.errors, props.field.name);

  const { error, helperText, field, form, ...rest } = props;

  return (
    <TextField data-testid="TextField-aqzh"
      error={error ?? Boolean(isTouched && errorMessage)}
      helperText={helperText ?? ((isTouched && errorMessage) ? errorMessage : undefined)}
      {...rest}
      {...field}
      fullWidth
      variant="outlined"
      sx={authFieldSx}
      InputLabelProps={authLabelProps}
      type={showPassword ? 'text' : 'password'} // Toggle between text and password
      onKeyDown={(event) => handlePasswordVisibilityShortcut(event, () => setShowPassword(value => !value))}
      inputProps={{ 'aria-keyshortcuts': 'Alt+Shift+V' }}
      InputProps={{
        sx: authInputSx,
        endAdornment: (
          <InputAdornment position="end">
            <IconButton data-testid="IconButton-wc9o"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              title={`${showPassword ? 'Hide' : 'Show'} password (Alt+Shift+V)`}
              // Keep Tab moving between fields; the keyboard shortcut operates this control.
              tabIndex={-1}
              onClick={() => setShowPassword(!showPassword)}
              onMouseDown={(event) => event.preventDefault()}
              edge="end"
            >
              {showPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
};
